// export default TopNavigation;
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import ShareOptionsModal from "./Modal";

const TopNavigation = ({
  onSubmit,
  onSaveDraft,
  hasLoadedDraft = false,
  onOpenDraftAction,
  loading,
  postType,
  title,
  content,
  // Chapter-specific props
  chapterTitles = [],
  chapterContents = [],
  // Scene-specific props
  sceneTitles = [],
  sceneContents = [],
}) => {
  
  const navigate = useNavigate();
  const [showShareModal, setShowShareModal] = useState(false);

  const handleGoBack = () => {
    navigate(-1);
  };

  // Validation logic for chapter-based types (Novel, Book, Story)
  const validateChapterType = () => {
    // 1. Title must be filled
    if (!title.trim()) {
      return false;
    }

    // 2. Build array of complete chapters - BUG FIX: Changed from 100 to 50 chars minimum
    const completeChapters = [];
    for (let i = 0; i < chapterTitles.length; i++) {
      const hasTitle = chapterTitles[i]?.trim();
      const hasContent = chapterContents[i]?.trim();
      const meetsMinLength = chapterContents[i]?.trim().length >= 50; // FIXED: was 100, now 50

      if (hasTitle && !hasContent) return false;
      if (hasContent && !hasTitle) return false;
      if (hasTitle && hasContent && !meetsMinLength) return false;

      if (hasTitle && hasContent && meetsMinLength) {
        completeChapters.push(i);
      }
    }

    // 3. Must have at least Chapter 1 complete (index 0)
    if (completeChapters.length === 0 || !completeChapters.includes(0)) {
      return false;
    }

    // 4. All chapters must be sequential from Chapter 1
    for (let i = 0; i < completeChapters.length; i++) {
      if (completeChapters[i] !== i) {
        return false;
      }
    }

    return true;
  };

  // Validation logic for scene-based types (Script)
  const validateSceneType = () => {
    // 1. Title must be filled
    if (!title.trim()) {
      return false;
    }

    // 2. Build array of complete scenes
    const completeScenes = [];
    for (let i = 0; i < sceneTitles.length; i++) {
      const hasTitle = sceneTitles[i]?.trim();
      const hasContent = sceneContents[i]?.trim();
      const meetsMinLength = sceneContents[i]?.trim().length >= 50;

      if (hasTitle && !hasContent) return false;
      if (hasContent && !hasTitle) return false;
      if (hasTitle && hasContent && !meetsMinLength) return false;

      if (hasTitle && hasContent && meetsMinLength) {
        completeScenes.push(i);
      }
    }

    // 3. Must have at least Scene 1 complete (index 0)
    if (completeScenes.length === 0 || !completeScenes.includes(0)) {
      return false;
    }

    // 4. All scenes must be sequential from Scene 1
    for (let i = 0; i < completeScenes.length; i++) {
      if (completeScenes[i] !== i) {
        return false;
      }
    }

    return true;
  };

  // Validation logic for regular posts
  const validateRegularPost = () => {
    return title.trim() && content.trim();
  };

  // Determine if post button should be disabled
  const isChapterType = ["Novel", "Book", "Story"].includes(postType);
  const isSceneType = ["Script"].includes(postType);

  const isDisabled =
    loading ||
    !postType ||
    (isChapterType
      ? !validateChapterType()
      : isSceneType
      ? !validateSceneType()
      : !validateRegularPost());

  // BUG FIX #3: Fixed tooltip message to show correct minimum (50 chars, not 100)
  const getTooltipMessage = () => {
    if (!postType) return "Please select a post type";
    
    if (isChapterType) {
      if (!title.trim()) return "Complete title";
      return "Complete at least Chapter 1 (title + min 50 chars content)"; 
    }
    
    if (isSceneType) {
      if (!title.trim()) return "Complete title";
      return "Complete at least Scene 1 (title + min 50 chars content)";
    }
    
    if (!title.trim()) return "Complete title";
    if (!content.trim()) return "Add content";
    return "";
  };

  return (
    <div className="p-3 sm:p-4 flex items-center justify-between sticky top-0 bg-white z-10">
      <div className="flex items-center gap-1 sm:gap-2 min-w-0">
        <button
          onClick={handleGoBack}
          className="text-black p-1.5 sm:p-2 hover:bg-gray-100 rounded-full flex-shrink-0"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="sm:w-5 sm:h-5"
          >
            <line x1="19" y1="12" x2="5" y2="12" />
            <polyline points="12 19 5 12 12 5" />
          </svg>
        </button>
        <h1 className="text-base sm:text-lg md:text-xl text-black font-semibold truncate">
          Create Post
        </h1>
      </div>
      <button
        onClick={() => {
          if (hasLoadedDraft) {
            onOpenDraftAction();
          } else {
            setShowShareModal(true);
          }
        }}
        disabled={isDisabled}
        className={`px-5 sm:px-6 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium
          transition-all duration-200 flex items-center gap-1 sm:gap-2 flex-shrink-0
          ${isDisabled
            ? "bg-gray-300 text-gray-500 cursor-not-allowed"
            : "bg-[#b0d284] text-black hover:bg-[#9ec170] shadow-md hover:shadow-lg active:scale-[0.98]"
          }`}
        title={getTooltipMessage()}
      >
        {loading ? (
          <>
            <div className="animate-spin h-3 w-3 sm:h-4 sm:w-4 border-2 border-black border-t-transparent rounded-full"></div>
            <span className="hidden sm:inline">Sharing...</span>
          </>
        ) : (
          <span className="font-semibold text-black font-sm">Share</span>
        )}
      </button>

      <ShareOptionsModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        loading={loading}
        onShareNow={(e) => {
          setShowShareModal(false);
          onSubmit(e);
        }}
        onSaveDraft={(e) => {
          setShowShareModal(false);
          onSaveDraft(e);
        }}
      />

    </div>
  );
};

export default TopNavigation;